import axios, { AxiosResponse } from 'axios';
import type { DateRange, User, ApiResponse, AuthenticatedUser, UserSuggestion } from '../types';

// In production, use relative URLs (same domain). In development, use localhost:8080
const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to log requests with AbortSignal
api.interceptors.request.use(
  (config) => {
    if (config.signal) {
      const url = config.url || 'unknown';
      const method = config.method?.toUpperCase();
      console.log(`[API] Request started: ${method} ${url}`);

      // Add abort listener to log cancellations
      const signal = config.signal as AbortSignal;
      signal.addEventListener('abort', () => {
        console.log(`[API] Request cancelled: ${method} ${url}`);
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle session expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log if request was cancelled
    if (axios.isCancel(error)) {
      console.log('[API] Request was cancelled:', error.message);
      return Promise.reject(error);
    }

    // If we get a 401 (Unauthorized), the session has expired
    if (error.response && error.response.status === 401) {
      // Dispatch a custom event that App.js can listen to
      window.dispatchEvent(new CustomEvent('session-expired'));
    }
    return Promise.reject(error);
  }
);

// Date params for API requests
interface DateParams {
  startTime?: string;
  endTime?: string;
  refresh?: string;
}

// Helper function to build params with absolute timestamps and refresh flag
const buildDateParams = (
  dateRange: DateRange | null = null,
  refresh: boolean = false
): DateParams => {
  const params: DateParams = {};

  // Use provided dateRange or default to last 2 hours
  if (dateRange && dateRange.start && dateRange.end) {
    params.startTime = dateRange.start;
    params.endTime = dateRange.end;
  } else {
    // Default: last 2 hours
    params.startTime = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
    params.endTime = new Date().toISOString();
  }

  if (refresh) {
    params.refresh = 'true';
  }

  return params;
};

// Auth API
export const authAPI = {
  logout: (signal?: AbortSignal): Promise<AxiosResponse<void>> =>
    api.post('/auth/logout', {}, { signal }),
  getCurrentUser: (signal?: AbortSignal): Promise<AxiosResponse<AuthenticatedUser>> =>
    api.get('/auth/me', { signal }),
};

// Unified Stats API - supports both current user (no email) and specific user (with email)
export const statsAPI = {
  // Get device info - should be called first
  getDeviceInfo: (
    email: string | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/devices` : '/stats/devices';
    console.log(
      `[API] getDeviceInfo called for ${email || 'current user'} with refresh: ${refresh}`
    );
    return api.get(endpoint, { params: refresh ? { refresh: 'true' } : {}, signal });
  },

  getAlerts: (
    email: string | null = null,
    dateRange: DateRange | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/alerts` : '/stats/alerts';
    console.log(
      `[API] getAlerts called for ${email || 'current user'} with dateRange:`,
      dateRange,
      'refresh:',
      refresh
    );
    return api.get(endpoint, { params: buildDateParams(dateRange, refresh), signal });
  },

  getRecommendations: (
    email: string | null = null,
    dateRange: DateRange | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/recommendations` : '/stats/recommendations';
    console.log(
      `[API] getRecommendations called for ${email || 'current user'} with dateRange:`,
      dateRange,
      'refresh:',
      refresh
    );
    return api.get(endpoint, { params: buildDateParams(dateRange, refresh), signal });
  },

  getZoomStats: (
    email: string | null = null,
    dateRange: DateRange | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/zoom` : '/stats/zoom';
    console.log(
      `[API] getZoomStats called for ${email || 'current user'} with dateRange:`,
      dateRange,
      'refresh:',
      refresh
    );
    return api.get(endpoint, { params: buildDateParams(dateRange, refresh), signal });
  },

  getZscalerStats: (
    email: string | null = null,
    dateRange: DateRange | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/zscaler` : '/stats/zscaler';
    console.log(
      `[API] getZscalerStats called for ${email || 'current user'} with dateRange:`,
      dateRange,
      'refresh:',
      refresh
    );
    return api.get(endpoint, { params: buildDateParams(dateRange, refresh), signal });
  },

  getMistStats: (
    email: string | null = null,
    dateRange: DateRange | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/mist` : '/stats/mist';
    console.log(
      `[API] getMistStats called for ${email || 'current user'} with dateRange:`,
      dateRange,
      'refresh:',
      refresh
    );
    return api.get(endpoint, { params: buildDateParams(dateRange, refresh), signal });
  },

  getTanium: (email: string | null = null, signal?: AbortSignal): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/tanium` : '/stats/tanium';
    console.log(`[API] getTanium called for ${email || 'current user'}`);
    return api.get(endpoint, { signal });
  },

  getNetworkInfrastructure: (
    email: string | null = null,
    siteId: string,
    dateRange: DateRange | null = null,
    refresh: boolean = false,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email
      ? `/stats/${email}/network-infrastructure`
      : '/stats/network-infrastructure';
    const params = { siteId, ...buildDateParams(dateRange, refresh) };
    return api.get(endpoint, { params, signal });
  },

  // Thresholds (public - any authenticated user)
  getThresholds: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/stats/thresholds', { signal }),

  // Active announcement banners (public - any authenticated user)
  getBanners: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/stats/banners', { signal }),
  dismissBanner: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/stats/banners/${id}/dismiss`),

  getUserDetails: (
    email: string | null = null,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const endpoint = email ? `/stats/${email}/user-details` : '/stats/user-details';
    return api.get(endpoint, { signal });
  },
};

// Feedback data structure
interface FeedbackData {
  rating: number;
  message?: string;
  [key: string]: any;
}

// Feedback API
export const feedbackAPI = {
  submitFeedback: (feedbackData: FeedbackData, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.post('/feedback', feedbackData, { signal }),
  getMyFeedback: (limit: number = 10, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/feedback/my-feedback', { params: { limit }, signal }),
  getAllFeedback: (
    limit: number = 50,
    offset: number = 0,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => api.get('/feedback', { params: { limit, offset }, signal }),
  getFeedbackStats: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/feedback/stats', { signal }),
};

// Admin API filters
interface AdminFilters {
  [key: string]: any;
}

// Announcement banner create/update payload
export interface BannerInput {
  heading: string;
  subheading: string | null;
  type: 'info' | 'warning' | 'critical';
  start_at: string;
  end_at: string | null;
  non_dismissible: boolean;
}

// Admin API
export const adminAPI = {
  // Users
  getUsers: (
    limit: number = 50,
    offset: number = 0,
    search: string = '',
    role: string = '',
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => {
    const params: Record<string, any> = { limit, offset };
    if (search) params.search = search;
    if (role) params.role = role;
    return api.get('/admin/users', { params, signal });
  },
  getUserById: (id: string, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get(`/admin/users/${id}`, { signal }),
  updateUserRole: (id: string, role: string, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.patch(`/admin/users/${id}/role`, { role }, { signal }),
  getUsersStats: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/admin/users/stats', { signal }),

  // Feedback
  getFeedback: (
    limit: number = 50,
    offset: number = 0,
    filters: AdminFilters = {},
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> =>
    api.get('/admin/feedback', { params: { limit, offset, ...filters }, signal }),
  getFeedbackById: (id: string, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get(`/admin/feedback/${id}`, { signal }),

  // Activity Logs
  getActivityLogs: (
    limit: number = 100,
    offset: number = 0,
    filters: AdminFilters = {},
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> =>
    api.get('/admin/activity', { params: { limit, offset, ...filters }, signal }),
  getActivityLogById: (id: string, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get(`/admin/activity/${id}`, { signal }),

  // User suggestions (autocomplete) - available to ADMIN and VIEWER
  suggestUsers: (
    query: string,
    signal?: AbortSignal
  ): Promise<AxiosResponse<ApiResponse<UserSuggestion[]>>> =>
    api.get('/admin/users/suggest', { params: { q: query }, signal }),

  // Devices
  getDevices: (
    limit: number = 50,
    offset: number = 0,
    filters: AdminFilters = {},
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> =>
    api.get('/admin/devices', { params: { limit, offset, ...filters }, signal }),
  getDeviceById: (id: string, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get(`/admin/devices/${id}`, { signal }),
  getDevicesStats: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/admin/devices/stats', { signal }),

  // Thresholds
  getThresholds: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/admin/thresholds', { signal }),
  updateThreshold: (
    id: number,
    thresholds: { green_threshold: number; yellow_threshold: number; red_threshold: number },
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => api.put(`/admin/thresholds/${id}`, thresholds, { signal }),

  // Announcement banners
  getAdminBanners: (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get('/admin/banners', { signal }),
  getAdminBanner: (id: string, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.get(`/admin/banners/${id}`, { signal }),
  createBanner: (banner: BannerInput, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.post('/admin/banners', banner, { signal }),
  updateBanner: (
    id: number,
    banner: BannerInput,
    signal?: AbortSignal
  ): Promise<AxiosResponse<any>> => api.put(`/admin/banners/${id}`, banner, { signal }),
  deleteBanner: (id: number, signal?: AbortSignal): Promise<AxiosResponse<any>> =>
    api.delete(`/admin/banners/${id}`, { signal }),
};

// SSE Stream URL builder
export function getStreamUrl(
  email: string | null,
  dateRange: DateRange,
  refresh?: boolean,
  deviceId?: string | null
): string {
  const base = import.meta.env.PROD
    ? '/api'
    : import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

  const path = email ? `/stats/${email}/stream` : '/stats/stream';
  const params = new URLSearchParams();

  if (dateRange.start) params.set('startTime', dateRange.start);
  if (dateRange.end) params.set('endTime', dateRange.end);
  if (refresh) params.set('refresh', 'true');
  if (deviceId) params.set('deviceId', deviceId);

  return `${base}${path}?${params.toString()}`;
}

// Health check
export const healthCheck = (signal?: AbortSignal): Promise<AxiosResponse<any>> =>
  api.get('/health', { signal });

export default api;
